import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Alert, Chip, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import useTableState from '../../hooks/useTableState';
import { facetFilterOptions } from '../common/facetFilter';
import { getReferralLeads } from '../../api/referralLinkApi';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';

/**
 * Referral Leads — everybody who filled in a referrer's link, and what became of them.
 *
 * (user 2026-10-31: "i think under referral we make it referral leads. so the referral
 *  leads might become customers ... then each referral leads is by which referral. then
 *  the referral is customer or not, previousy the sales isit cobroke or agent or not")
 *
 * A different question from the two screens beside it, which is why it is a third:
 *   · Referral          — the PEOPLE who refer. One row per referrer.
 *   · My Referral Report — the MONEY. Built from sales, so a lead that never sold is invisible.
 *   · Referral Leads    — the INTRODUCTIONS. One row per person sent in, sold or not.
 *
 * "Became a sale" is not a status somebody sets. It is ReferralEnquiry.SalesId, stamped
 * when the sale is created from the lead — so the list cannot say a lead converted while
 * no sale exists, and cannot say it didn't while one does.
 *
 * Same MaterialReactTable setup as the referrer list and the user list, deliberately:
 * three screens in one section that behaved differently would read as three apps.
 */

const FONT = "'Poppins', sans-serif";

const chip = (label, bg, fg) => (
    <Chip size="small" label={label} sx={{
        fontFamily: FONT, fontWeight: 700, fontSize: '0.64rem', height: 20,
        backgroundColor: bg, color: fg,
    }} />
);

export default function ReferralLeadsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const ts = useTableState('referralLeads');
    const navigate = useNavigate();

    const load = useCallback(() => {
        setLoading(true);
        getReferralLeads()
            .then(res => setRows(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setError('Could not load the referral leads. Please try again.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    /* Open create-sales with the lead already in it. The customer's name and phone come
       across, the Referral picker is pre-selected to whoever introduced them, and the lead
       id rides along so the sale can be stamped back onto it on success. Nothing is
       written here — a lead only converts when a sale actually exists. */
    const createSale = (lead) => {
        navigate('/sales/create', {
            state: {
                rscPrefill: {
                    customerName: lead.name || '',
                    customerPhone: lead.phone || '',
                    customerEmail: lead.email || '',
                    selectedReferralId: lead.referrerId || '',
                },
                referralLeadId: lead.referralEnquiryId,
            },
        });
    };

    const columns = useMemo(() => [
        {
            // The lead is a customer record from the moment it arrives, so the name opens
            // it. Older leads, made before that, have no customer to open.
            accessorKey: 'name', header: 'Lead', size: 170,
            Cell: ({ row, cell }) => {
                const v = cell.getValue() || '—';
                if (!row.original.customerId) return v;
                return (
                    <span
                        onClick={(e) => { e.stopPropagation(); navigate(`/sales/customers/${row.original.customerId}`); }}
                        style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500 }}
                    >
                        {v}
                    </span>
                );
            },
        },
        { accessorKey: 'phone', header: 'Phone', size: 130, Cell: ({ cell }) => cell.getValue() || '—' },
        { accessorKey: 'location', header: 'Where', size: 120, Cell: ({ cell }) => cell.getValue() || '—' },
        {
            accessorKey: 'referrerName', header: 'Referred by', size: 170,
            Cell: ({ cell }) => cell.getValue() || '—',
        },
        {
            // Did the referrer buy from us themselves? Matched on their phone or IC against
            // the customer on a live sale — a referrer who is also a customer is a different
            // conversation from one who has only ever signed up.
            id: 'referrerIsCustomer', header: 'Referrer is a customer', size: 150,
            accessorFn: (r) => (r.referrerIsCustomer ? 'Yes' : 'No'),
            Cell: ({ row }) => (row.original.referrerIsCustomer
                ? chip(row.original.referrerSaleId ? `Yes · #${row.original.referrerSaleId}` : 'Yes', '#DBEAFE', '#1E40AF')
                : chip('No', '#F1F5F9', '#64748B')),
        },
        {
            accessorKey: 'createdOn', header: 'Sent', size: 105,
            Cell: ({ cell }) => (cell.getValue() ? convertDateTimeToDateString(cell.getValue()) : '—'),
        },
        {
            // Three states, not two. The same customer can be introduced by several
            // referrers; only one introduction becomes the sale, and the others should
            // stop reading as unworked leads the moment it does.
            id: 'status', header: 'Status', size: 140,
            accessorFn: (r) => (r.salesId ? 'Became a sale'
                : r.rivalSalesId ? 'Sold by someone else' : 'With the team'),
            Cell: ({ row }) => {
                const r = row.original;
                if (r.salesId) return chip('Became a sale', '#D1FAE5', '#065F46');
                if (r.rivalSalesId) {
                    return (
                        <Box>
                            {chip(`Sold by someone else · #${r.rivalSalesId}`, '#F1F5F9', '#64748B')}
                            {r.rivalReferrerName && (
                                <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: 2 }}>
                                    through {r.rivalReferrerName}
                                </div>
                            )}
                        </Box>
                    );
                }
                return chip('With the team', '#FEF3C7', '#92400E');
            },
        },
        {
            id: 'sale', header: 'Sale', size: 150,
            accessorFn: (r) => (r.salesId ? `#${r.salesId}` : ''),
            Cell: ({ row }) => {
                const r = row.original;
                if (!r.salesId) return '—';
                return (
                    <Box>
                        <span style={{ color: '#1A8CFF', fontWeight: 600 }}>#{r.salesId}</span>
                        {r.saleFinalPrice != null && (
                            <div style={{ fontSize: '0.72rem', color: '#64748B' }}>
                                {handlePriceWithRM(Number(r.saleFinalPrice) || 0)}
                            </div>
                        )}
                    </Box>
                );
            },
        },
        {
            // Who sold it, and whether it was split. Read off the sale's own agent and
            // co-agent, so this can never disagree with the sale page.
            id: 'soldBy', header: 'Sold by', size: 190,
            accessorFn: (r) => (r.isCoBroke
                ? `${r.saleAgentName || ''} + ${r.saleCoAgentName || ''} (co-broke)`
                : (r.saleAgentName || '')),
            Cell: ({ row }) => {
                const r = row.original;
                if (!r.salesId) return '—';
                return (
                    <Box>
                        <div>{r.isCoBroke ? `${r.saleAgentName} + ${r.saleCoAgentName}` : (r.saleAgentName || '—')}</div>
                        {chip(r.isCoBroke ? 'Co-broke' : 'Single agent',
                            r.isCoBroke ? '#EDE9FE' : '#F1F5F9',
                            r.isCoBroke ? '#5B21B6' : '#64748B')}
                    </Box>
                );
            },
        },
        {
            id: 'actions', header: 'Actions', size: 110,
            enableColumnFilter: false, enableSorting: false,
            Cell: ({ row }) => {
                const r = row.original;
                // Nothing to do on a lead somebody else already closed — offering
                // "Create sale" there invites a second sale for the same customer.
                if (!r.salesId && r.rivalSalesId) return <span style={{ color: '#94A3B8' }}>—</span>;
                return (
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            if (r.salesId) navigate(`/accounting/sales/${r.salesId}`);
                            else createSale(r);
                        }}
                        style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
                    >
                        {r.salesId ? 'Open sale' : 'Create sale'}
                    </span>
                );
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [navigate]);

    const table = useMaterialReactTable({
        ...facetFilterOptions,
        columns,
        data: rows,
        enableRowSelection: false,
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true,
        initialState: { density: 'compact', showGlobalFilter: true },
        state: { globalFilter: ts.globalFilter, sorting: ts.sorting, pagination: ts.pagination, columnFilters: ts.columnFilters },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableContainerProps: { sx: { overflowX: 'auto', maxHeight: 'unset' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: FONT, padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: FONT, padding: '6px 10px' } },
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        muiSearchTextFieldProps: { placeholder: 'Search leads...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: FONT } } },
        muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50, 100] },
        renderEmptyRowsFallback: () => (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                    No leads yet
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#64748B' }}>
                    These arrive when somebody fills in a referrer’s link. Each referrer finds
                    theirs on their own My Referrals page.
                </Typography>
            </Box>
        ),
    });

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.4rem', color: '#0F172A', mb: 0.5 }}>
                    Referral Leads
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#64748B' }}>
                    People sent in through a referrer’s link. “Create sale” carries the name and
                    phone into a new sale and records it against the lead.
                </Typography>
            </Box>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <MaterialReactTable table={table} />
        </Box>
    );
}
